import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Plus, Search, AlertTriangle, AlertCircle, Clock, 
  Bug, CheckCircle, Loader2, Eye
} from 'lucide-react';

const TIPOS_OCORRENCIA = [
  { value: 'tentativa_indevida', label: 'Tentativa Indevida', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'atraso', label: 'Atraso', icon: Clock, color: 'text-amber-600' },
  { value: 'erro_sistema', label: 'Erro de Sistema', icon: Bug, color: 'text-blue-600' },
  { value: 'excecao', label: 'Exceção', icon: AlertCircle, color: 'text-purple-600' },
];

export default function DnaGoOcorrencias() {
  const [ocorrencias, setOcorrencias] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolverDialog, setResolverDialog] = useState({ open: false, ocorrencia: null });
  const [resolucao, setResolucao] = useState('');
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    tipo: 'excecao',
    descricao: '',
    aluno_id: '',
    autorizado_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ocorrenciasRes, alunosRes] = await Promise.all([
        api.get('/dnago/ocorrencias?limit=200'),
        api.get('/students')
      ]);
      setOcorrencias(ocorrenciasRes.data || []);
      setAlunos(alunosRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('tipo', formData.tipo);
      params.append('descricao', formData.descricao);
      if (formData.aluno_id) params.append('aluno_id', formData.aluno_id);

      await api.post(`/dnago/ocorrencias?${params.toString()}`);
      toast.success('Ocorrência registrada!');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    } finally {
      setSaving(false);
    }
  };

  const handleResolver = async () => {
    if (!resolucao.trim()) {
      toast.error('Informe a resolução');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/dnago/ocorrencias/${resolverDialog.ocorrencia.id}/resolver`, null, {
        params: { resolucao }
      });
      toast.success('Ocorrência resolvida!');
      setResolverDialog({ open: false, ocorrencia: null });
      setResolucao('');
      loadData();
    } catch (error) {
      toast.error('Erro ao resolver ocorrência');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'excecao',
      descricao: '',
      aluno_id: '',
      autorizado_id: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoConfig = (tipo) => {
    return TIPOS_OCORRENCIA.find(t => t.value === tipo) || TIPOS_OCORRENCIA[3];
  };

  const filteredOcorrencias = ocorrencias.filter(o => {
    const matchSearch = 
      o.descricao?.toLowerCase().includes(search.toLowerCase()) ||
      o.aluno_nome?.toLowerCase().includes(search.toLowerCase());
    const matchTipo = filtroTipo === 'todos' || o.tipo === filtroTipo;
    const matchStatus = filtroStatus === 'todos' || 
      (filtroStatus === 'pendentes' && !o.resolvido) ||
      (filtroStatus === 'resolvidas' && o.resolvido);
    return matchSearch && matchTipo && matchStatus;
  });

  const stats = {
    total: ocorrencias.length,
    pendentes: ocorrencias.filter(o => !o.resolvido).length,
    resolvidas: ocorrencias.filter(o => o.resolvido).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
            Ocorrências
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Registro de incidentes e exceções
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Ocorrência
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.pendentes}</p>
                <p className="text-xs text-gray-500">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.resolvidas}</p>
                <p className="text-xs text-gray-500">Resolvidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar ocorrências..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os tipos</SelectItem>
                {TIPOS_OCORRENCIA.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendentes">Pendentes</SelectItem>
                <SelectItem value="resolvidas">Resolvidas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-32">Data/Hora</TableHead>
                  <TableHead className="w-40">Tipo</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="hidden md:table-cell">Aluno</TableHead>
                  <TableHead className="hidden lg:table-cell">Operador</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOcorrencias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      Nenhuma ocorrência encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOcorrencias.map((ocorrencia) => {
                    const tipoConfig = getTipoConfig(ocorrencia.tipo);
                    const TipoIcon = tipoConfig.icon;
                    
                    return (
                      <TableRow key={ocorrencia.id} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm">
                          {formatDate(ocorrencia.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 ${tipoConfig.color}`}>
                            <TipoIcon className="w-4 h-4" />
                            <span className="text-sm">{tipoConfig.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {ocorrencia.descricao}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {ocorrencia.aluno_nome || '-'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-gray-500">
                          {ocorrencia.operador_nome}
                        </TableCell>
                        <TableCell>
                          {ocorrencia.resolvido ? (
                            <Badge className="bg-green-100 text-green-700">Resolvida</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">Pendente</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!ocorrencia.resolvido && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setResolverDialog({ open: true, ocorrencia })}
                            >
                              Resolver
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Nova Ocorrência */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Nova Ocorrência
            </DialogTitle>
            <DialogDescription>
              Registre um incidente ou exceção
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo de Ocorrência</Label>
              <Select
                value={formData.tipo}
                onValueChange={(v) => setFormData({ ...formData, tipo: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_OCORRENCIA.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Aluno (opcional)</Label>
              <Select
                value={formData.aluno_id}
                onValueChange={(v) => setFormData({ ...formData, aluno_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um aluno" />
                </SelectTrigger>
                <SelectContent>
                  {alunos.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Descrição *</Label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva a ocorrência em detalhes..."
                rows={4}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Registrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Resolver */}
      <Dialog open={resolverDialog.open} onOpenChange={(open) => !open && setResolverDialog({ open: false, ocorrencia: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Resolver Ocorrência
            </DialogTitle>
          </DialogHeader>

          {resolverDialog.ocorrencia && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium">{resolverDialog.ocorrencia.descricao}</p>
                <p className="text-gray-500 mt-1">{formatDate(resolverDialog.ocorrencia.created_at)}</p>
              </div>

              <div>
                <Label>Resolução *</Label>
                <Textarea
                  value={resolucao}
                  onChange={(e) => setResolucao(e.target.value)}
                  placeholder="Como a ocorrência foi resolvida?"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolverDialog({ open: false, ocorrencia: null })}>
              Cancelar
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleResolver} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Marcar como Resolvida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
